import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import { apiActions } from "redux/_core/api";
import reportAPIS from "redux/app/report/api";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	Brush,
} from "recharts";

// ─── Types ───────────────────────────────────────────────────────────────────

interface VesselOption {
	value: string;       // vesselId
	label: string;       // vesselName
	lngFleetType: string; // e.g. "CMA Cont", "PCTC", "Bulk 210k" …
}

interface LngDoc {
	Date: string;
	pkiMn?: number | string;
	ROB?: number | string;
	Discrepancy?: number | string;
	vesselId: string;
	[key: string]: any;
}

interface VesselResult {
	vesselId: string;
	docs: LngDoc[];
	error?: string;
}

interface ChartDataPoint {
	date: string;
	[vesselName: string]: number | string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_FLEETS = "__ALL__";

const LINE_COLORS = [
	"#2563eb", "#f97316", "#16a34a", "#0891b2",
	"#9333ea", "#dc2626", "#ca8a04", "#0d9488", "#7c3aed",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDisplayDate(dateStr: string): string {
	if (!dateStr) return "";
	return dateStr.slice(0, 10);
}

function buildChartData(
	vesselResults: VesselResult[],
	vessels: VesselOption[],
	field: string
): ChartDataPoint[] {
	const vesselIdToName: Record<string, string> = {};
	vessels.forEach((v) => { vesselIdToName[v.value] = v.label; });

	const dateSet = new Set<string>();
	vesselResults.forEach(({ docs }) => {
		docs.forEach((doc) => { if (doc.Date) dateSet.add(toDisplayDate(doc.Date)); });
	});

	return Array.from(dateSet).sort().map((date) => {
		const point: ChartDataPoint = { date };
		vesselResults.forEach(({ vesselId, docs }) => {
			const name = vesselIdToName[vesselId] ?? vesselId;
			const doc = docs.find((d) => toDisplayDate(d.Date) === date);
			if (doc && doc[field] !== undefined) {
				const val = parseFloat(String(doc[field]));
				if (!isNaN(val)) {
					point[name] = val;
				}
			}
		});
		return point;
	});
}

// ─── Sub-component: TrendChart ────────────────────────────────────────────────

interface TrendChartProps {
	title: string;
	data: ChartDataPoint[];
	vesselNames: string[];
	yAxisDomain?: [number | string, number | string];
	onClick?: () => void;
	isExpanded?: boolean;
}

function TrendChart({ title, data, vesselNames, yAxisDomain, onClick, isExpanded }: TrendChartProps) {
	return (
		<div 
			className={isExpanded ? "w-full h-full" : `bg-white border border-gray-200 rounded-lg p-4 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
			onClick={onClick}
		>
			{!isExpanded && <h3 className="text-sm font-semibold text-gray-700 mb-3 text-center">{title}</h3>}
			<ResponsiveContainer width="100%" height={isExpanded ? 500 : 220}>
				<LineChart data={data} margin={{ top: 4, right: 20, left: 0, bottom: isExpanded ? 20 : 4 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
					<XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
					<YAxis tick={{ fontSize: 10 }} width={45} domain={yAxisDomain} />
					<Tooltip contentStyle={{ fontSize: 11 }} labelFormatter={(l) => `Date: ${l}`} />
					<Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="top" />
					{isExpanded && <Brush dataKey="date" height={30} stroke="#4b5563" tickFormatter={(v) => v.slice(5)} />}
					{vesselNames.map((name, i) => (
						<Line
							key={name}
							type="monotone"
							dataKey={name}
							stroke={LINE_COLORS[i % LINE_COLORS.length]}
							dot={{ r: 3 }}
							activeDot={{ r: 5 }}
							connectNulls
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
			{!isExpanded && onClick && (
				<div className="text-center mt-2 text-xs text-gray-400">Click to expand and zoom</div>
			)}
		</div>
	);
}

// ─── Main component ───────────────────────────────────────────────────────────

type OwnProps = {};
type LngReportProps = PropsFromRedux & OwnProps;

function LngReport(props: LngReportProps) {
	const { dispatch } = props;

	// Date range — default last 7 days
	const today = new Date();
	const sevenDaysAgo = new Date(today);
	sevenDaysAgo.setDate(today.getDate() - 7);
	const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

	const [fromDate, setFromDate] = useState<string>(fmtDate(sevenDaysAgo));
	const [toDate, setToDate]     = useState<string>(fmtDate(today));

	// All DF vessels (those that have lngFleetType)
	const [allVessels, setAllVessels] = useState<VesselOption[]>([]);

	// Fleet type grouping filter
	const [selectedFleetType, setSelectedFleetType] = useState<string>(ALL_FLEETS);

	// Vessel selector
	const [vesselSearch, setVesselSearch]       = useState<string>("");
	const [selectedVessels, setSelectedVessels] = useState<VesselOption[]>([]);
	const [isDropdownOpen, setIsDropdownOpen]   = useState<boolean>(false);

	// Report state
	const [isLoading, setIsLoading]       = useState<boolean>(false);
	const [error, setError]               = useState<string | null>(null);
	const [vesselResults, setVesselResults] = useState<VesselResult[]>([]);
	const [hasGenerated, setHasGenerated] = useState<boolean>(false);

	// Expanded chart modal state
	const [expandedChart, setExpandedChart] = useState<"MN" | "Discrepancy" | "ROB" | null>(null);

	// ── Load vessel list on mount
	useEffect(() => {
		dispatch(
			apiActions.apiRequest({
				...reportAPIS.loadLngVesselList(),
				postExecute: {
					onSuccess({ data }) {
						// Filter: only keep vessels that have lngFleetType
						const all: VesselOption[] = (data?.vessels || data || [])
							.filter((v: any) => v.lngFleetType)
							.map((v: any) => ({
								value: v.id ?? v._id,
								label: v.vesselName ?? v.name ?? v.id,
								lngFleetType: v.lngFleetType,
							}));
						setAllVessels(all);
					},
				},
			})
		);
	}, [dispatch]);

	// ── Distinct fleet types for the dropdown (sorted)
	const fleetTypes = useMemo<string[]>(() => {
		const set = new Set(allVessels.map((v) => v.lngFleetType));
		return Array.from(set).sort();
	}, [allVessels]);

	// ── Vessels visible after fleet-type filter + search
	const filteredVessels = useMemo<VesselOption[]>(() => {
		let list = allVessels;
		if (selectedFleetType !== ALL_FLEETS) {
			list = list.filter((v) => v.lngFleetType === selectedFleetType);
		}
		if (vesselSearch.trim()) {
			list = list.filter((v) =>
				v.label.toLowerCase().includes(vesselSearch.toLowerCase())
			);
		}
		return list;
	}, [allVessels, selectedFleetType, vesselSearch]);

	// ── Toggle single vessel
	const toggleVessel = useCallback((vessel: VesselOption) => {
		setSelectedVessels((prev) => {
			const exists = prev.find((v) => v.value === vessel.value);
			return exists ? prev.filter((v) => v.value !== vessel.value) : [...prev, vessel];
		});
	}, []);

	// ── Deselect by id
	const deselectVessel = (vesselId: string) =>
		setSelectedVessels((prev) => prev.filter((v) => v.value !== vesselId));

	// ── Select all visible
	const handleSelectAll = () => {
		setSelectedVessels((prev) => {
			const toAdd = filteredVessels.filter(
				(v) => !prev.some((s) => s.value === v.value)
			);
			return [...prev, ...toAdd];
		});
	};

	// ── When fleet type filter changes, drop selections no longer in view
	const handleFleetTypeChange = (ft: string) => {
		setSelectedFleetType(ft);
		if (ft !== ALL_FLEETS) {
			setSelectedVessels((prev) => prev.filter((v) => v.lngFleetType === ft));
		}
	};

	// ── Generate report
	const generateReport = () => {
		if (selectedVessels.length === 0) {
			setError("Please select at least one vessel.");
			return;
		}
		setIsLoading(true);
		setError(null);
		setHasGenerated(false);

		dispatch(
			apiActions.apiRequest({
				...reportAPIS.loadLngReport(
					selectedVessels.map((v) => v.value),
					fromDate,
					toDate
				),
				postExecute: {
					onSuccess({ data }) {
						setVesselResults(data?.data || []);
						setHasGenerated(true);
					},
					onError(err: any) {
						setError(err?.message ?? err?.error ?? "Failed to fetch LNG data");
					},
					finally() {
						setIsLoading(false);
					},
				},
			})
		);
	};

	// ── Chart data
	const selectedNames = selectedVessels.map((v) => v.label);
	const robData  = hasGenerated ? buildChartData(vesselResults, selectedVessels, "ROB_cal")    : [];
	const mnData   = hasGenerated ? buildChartData(vesselResults, selectedVessels, "MN")         : [];
	const discData = hasGenerated ? buildChartData(vesselResults, selectedVessels, "Discrepancy"): [];

	// ─── Render ───────────────────────────────────────────────────────────────

	return (
		<div className="bg-white m-2 rounded-lg h-[calc(100vh-98px)] overflow-y-auto">

			{/* Header */}
			<div className="flex items-center px-6 pt-6 pb-4 border-b border-gray-100">
				<span
					title="go back"
					onClick={() => window?.history?.back()}
					className="mr-3 text-lg cursor-pointer bp3-icon bp3-icon-arrow-left text-gray-600 hover:text-gray-900"
				/>
				<h2 className="text-xl font-semibold text-gray-800">LNG Report</h2>
				{allVessels.length > 0 && (
					<span className="ml-3 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 font-medium">
						{allVessels.length} DF vessel{allVessels.length !== 1 ? "s" : ""}
					</span>
				)}
			</div>

			{/* Filter Panel */}
			<div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
				<div className="flex flex-wrap gap-4 items-end">

					{/* From Date */}
					<div>
						<label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
						<input
							type="date"
							value={fromDate}
							onChange={(e) => setFromDate(e.target.value)}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					{/* To Date */}
					<div>
						<label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
						<input
							type="date"
							value={toDate}
							onChange={(e) => setToDate(e.target.value)}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					{/* Fleet Type Filter */}
					<div className="w-44">
						<label className="block text-xs font-medium text-gray-600 mb-1">Fleet Type</label>
						<select
							value={selectedFleetType}
							onChange={(e) => handleFleetTypeChange(e.target.value)}
							className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
						>
							<option value={ALL_FLEETS}>All Types</option>
							{fleetTypes.map((ft) => (
								<option key={ft} value={ft}>{ft}</option>
							))}
						</select>
					</div>

					{/* Vessel Selector */}
					<div className="w-64">
						<label className="block text-xs font-medium text-gray-600 mb-1">
							Select Vessels
							{allVessels.length === 0 && (
								<span className="ml-1 text-gray-400 font-normal">(loading…)</span>
							)}
						</label>
						<div className="relative">
							<input
								type="text"
								placeholder="Search vessel name…"
								value={vesselSearch}
								onFocus={() => setIsDropdownOpen(true)}
								onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
								onChange={(e) => { setVesselSearch(e.target.value); setIsDropdownOpen(true); }}
								className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>

							{isDropdownOpen && filteredVessels.length > 0 && (
								<div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
									{/* Select All row */}
									<div
										onMouseDown={handleSelectAll}
										className="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs text-blue-600 hover:bg-blue-50 border-b border-gray-100 font-medium"
									>
										Select all ({filteredVessels.length})
									</div>

									{/* Group by lngFleetType */}
									{selectedFleetType === ALL_FLEETS
										? fleetTypes
											.filter((ft) =>
												filteredVessels.some((v) => v.lngFleetType === ft)
											)
											.map((ft) => (
												<div key={ft}>
													<div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
														{ft}
													</div>
													{filteredVessels
														.filter((v) => v.lngFleetType === ft)
														.map((vessel) => {
															const isSelected = selectedVessels.some((s) => s.value === vessel.value);
															return (
																<div
																	key={vessel.value}
																	onMouseDown={() => toggleVessel(vessel)}
																	className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 ${isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700"}`}
																>
																	<input type="checkbox" readOnly checked={isSelected} className="pointer-events-none" />
																	{vessel.label}
																</div>
															);
														})}
												</div>
											))
										: filteredVessels.map((vessel) => {
											const isSelected = selectedVessels.some((s) => s.value === vessel.value);
											return (
												<div
													key={vessel.value}
													onMouseDown={() => toggleVessel(vessel)}
													className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 ${isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700"}`}
												>
													<input type="checkbox" readOnly checked={isSelected} className="pointer-events-none" />
													{vessel.label}
												</div>
											);
										})
									}
								</div>
							)}

							{isDropdownOpen && filteredVessels.length === 0 && allVessels.length > 0 && (
								<div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-sm text-gray-400">
									No vessels found
								</div>
							)}
						</div>
					</div>

					{/* Generate Button */}
					<div>
						<label className="block text-xs font-medium text-transparent mb-1 select-none">&nbsp;</label>
						<button
							onClick={generateReport}
							disabled={isLoading}
							className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{isLoading ? "Loading…" : "Generate Report"}
						</button>
					</div>
				</div>

				{/* Selected Vessel Chips */}
				{selectedVessels.length > 0 && (
					<div className="flex flex-wrap gap-2 mt-4">
						{selectedVessels.map((v) => (
							<span
								key={v.value}
								className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full"
							>
								{v.label}
								<button
									onClick={() => deselectVessel(v.value)}
									className="hover:text-blue-600 focus:outline-none ml-1"
									title="Remove"
								>
									<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
									</svg>
								</button>
							</span>
						))}
						<button
							onClick={() => setSelectedVessels([])}
							className="text-xs text-red-500 hover:text-red-700 underline self-center"
						>
							Clear all
						</button>
					</div>
				)}

				{/* Error */}
				{error && <p className="mt-3 text-sm text-red-600 font-medium">{error}</p>}
			</div>

			{/* Charts */}
			<div className="px-6 py-6">
				{isLoading && (
					<div className="flex justify-center items-center py-20 text-gray-500 text-sm">
						Fetching LNG data…
					</div>
				)}

				{!isLoading && !hasGenerated && (
					<div className="flex flex-col items-center justify-center py-20 text-gray-400">
						<svg className="w-12 h-12 mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
						</svg>
						<p className="text-sm">Select DF vessels and click Generate Report</p>
					</div>
				)}

				{!isLoading && hasGenerated && (
					<>
						{vesselResults.every((r) => r.docs.length === 0) ? (
							<div className="flex justify-center py-20 text-gray-400 text-sm">
								No LNG data found for the selected vessels and date range.
							</div>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<TrendChart title="MN Trend"          data={mnData}   vesselNames={selectedNames} yAxisDomain={[50, 90]} onClick={() => setExpandedChart("MN")} />
								<TrendChart title="Discrepancy Trend" data={discData} vesselNames={selectedNames} yAxisDomain={[-20, 20]} onClick={() => setExpandedChart("Discrepancy")} />
								<TrendChart title="ROB Trend"         data={robData}  vesselNames={selectedNames} onClick={() => setExpandedChart("ROB")} />
							</div>
						)}
					</>
				)}
			</div>

			{/* Modal for Expanded Chart */}
			{expandedChart && (
				<div 
					className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black bg-opacity-60 transition-opacity"
					onClick={() => setExpandedChart(null)}
				>
					<div 
						className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden flex flex-col"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
							<h3 className="text-lg font-bold text-gray-800">{expandedChart} Trend <span className="text-sm font-normal text-gray-500 ml-2">(Zoom active)</span></h3>
							<button 
								onClick={() => setExpandedChart(null)}
								className="text-gray-400 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
								title="Close"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
							</button>
						</div>
						<div className="p-6 h-[600px]">
							{expandedChart === "MN" && (
								<TrendChart title="" data={mnData} vesselNames={selectedNames} yAxisDomain={[50, 90]} isExpanded />
							)}
							{expandedChart === "Discrepancy" && (
								<TrendChart title="" data={discData} vesselNames={selectedNames} yAxisDomain={[-20, 20]} isExpanded />
							)}
							{expandedChart === "ROB" && (
								<TrendChart title="" data={robData} vesselNames={selectedNames} isExpanded />
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// ─── Redux ────────────────────────────────────────────────────────────────────

function mapStateToProps(_store: IStore, _ownProps: OwnProps) {
	return {};
}

function mapDispatchToProps(dispatch: Dispatch, _ownProps: OwnProps) {
	return { dispatch };
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(LngReport);
